// SPDX-License-Identifier: AGPL-3.0-or-later

import HoverPreloadScheduler, {type HoverPreloadKind} from '@app/features/app/state/HoverPreloadScheduler';
import {AccountScopedWork} from '@app/features/platform/state/AccountScopedWork';
import {useCallback, useEffect, useMemo, useRef} from 'react';

// Built outside the hook so the long-lived cancel callback does not share the hook's closure context, which holds the
// first render's preload callback and everything that callback captured (a channel, a guild).
function createCancel(kind: HoverPreloadKind, owner: object): () => void {
	return () => HoverPreloadScheduler.cancel(kind, owner);
}

export function useHoverPreload(kind: HoverPreloadKind, preload: () => void, delayMs: number, enabled = true) {
	const owner = useRef({}).current;
	const cancel = useMemo(() => createCancel(kind, owner), [kind, owner]);
	const preloadNow = useCallback(() => {
		cancel();
		if (enabled && !AccountScopedWork.isSuspended) preload();
	}, [cancel, enabled, preload]);
	const schedule = useCallback(() => {
		if (enabled) HoverPreloadScheduler.schedule(kind, owner, preloadNow, delayMs);
	}, [delayMs, enabled, kind, owner, preloadNow]);
	useEffect(() => cancel, [cancel, schedule]);
	return {schedule, cancel, preloadNow};
}
